import { CompatibilityFilter } from './CompatibilityFilter.js';
import { Option } from '../option.js';
import { PropertyPath } from '../engine.js';
import { engineUtils } from '../services/EngineUtils.js';

export type OptionsEntries = [string, Option[]][];


export type CallbackExitType = 'ResetFiltersAndContinueFromStart' | 'Exit' | void;

export class CSPSolver {
  constructor(
    private compatibilityFilter: CompatibilityFilter,
    private optionValues: Option[],
    private options: OptionsEntries,
  ) {
  }


  solve(optionPropertyPath: string, callback: (option: Option, options: Map<PropertyPath, Option>, q: Option[][]) => CallbackExitType) {
    const currentOptionValuesEntries = this.options;
    if (currentOptionValuesEntries.length == 0) {
      const map = new Map<PropertyPath, Option>();
      const copy = this.optionValues.slice();
      for (const option of this.optionValues) {
        map.set(optionPropertyPath, option);
        copy.shift();
        if (callback(option, map, [copy])) {
          return;
        }
      }
      return;
    }
    let currentOptionValue: Option;
    let allOptionsValues: Map<PropertyPath, Option> = new Map<PropertyPath, Option>();
    const fns: Array<(value: Option, q: Option[][]) => Option[]> = [];
    // TODO: We can put only tables related to current option, example: Door w\o glass should not put doorGlass table
    for (let index = 0; index < currentOptionValuesEntries.length; index++) {
      const [path, optionValues] = currentOptionValuesEntries[index];
      const prevOptionPath: PropertyPath = index > 0 ? currentOptionValuesEntries[index - 1][0] : optionPropertyPath;
      const isCurrentOption = index === 0;
      fns.push(
        (prevOptionValue: Option, q: Option[][]) => {
          if (isCurrentOption) {
            currentOptionValue = prevOptionValue;
          }
          allOptionsValues.set(prevOptionPath, prevOptionValue);
          this.compatibilityFilter.applyOption(prevOptionPath, prevOptionValue);
          return this.compatibilityFilter.filter(path, optionValues);
        }
      );
    }
    const lastEntry = currentOptionValuesEntries[currentOptionValuesEntries.length - 1];
    const lastOptionPath = lastEntry[0];
    engineUtils.cartesian(
      this.optionValues,
      fns,
      (lastOptionValue, q) => {
        allOptionsValues.set(lastOptionPath, lastOptionValue);

        this.compatibilityFilter.applyOption(lastOptionPath, lastOptionValue);
        if (this.compatibilityFilter.anyMath(lastOptionPath)) {
          const exit = callback(currentOptionValue, allOptionsValues, q);
          if (exit === 'Exit') {
            while (q.length) {
              q.pop();
            }
          }

          if (exit === 'ResetFiltersAndContinueFromStart') {
            while (q.length > 1) {
              const index = q.length - 1;
              const prevOptionPath: PropertyPath = index > 0 ? currentOptionValuesEntries[index - 1][0] : optionPropertyPath;
              this.compatibilityFilter.resetOption(prevOptionPath);
              allOptionsValues.delete(prevOptionPath);
              q.pop();
            }
          }
        }
      },
      (index) => {
        const prevOptionPath: PropertyPath = index > 0 ? currentOptionValuesEntries[index - 1][0] : optionPropertyPath;
        this.compatibilityFilter.resetOption(prevOptionPath);
        allOptionsValues.delete(prevOptionPath);
      });
  }
}
